import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Calendar, ChevronDown, AlertCircle, CalendarRange } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format, startOfMonth, endOfMonth, subMonths, subDays, differenceInCalendarDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CompactDateRangePickerProps {
    startDate: string;
    endDate: string;
    onDateChange: (start: string, end: string) => void;
    // Opt-in: adiciona o preset "15 dias" (default: false — nao afeta a Dash).
    include15Days?: boolean;
    // Opt-in: adiciona os presets "7 dias" e "30 dias" (hoje incluso). Default false.
    includeRolling?: boolean;
    // Opt-in: limita o range no modo Personalizado (em dias). Nao afeta os presets.
    maxRangeDays?: number;
    // Opt-in: so os atalhos, sem "Personalizado" nem calendario (telas cuja API so aceita periodos fixos).
    presetsOnly?: boolean;
    // Opt-in: atalho "Todo o período" (start e end vazios = sem recorte de data; SMS › Leads).
    includeAllTime?: boolean;
    // Opt-in: só estes atalhos (chaves de getPresets), na ordem de getPresets. Ex.: ATALHOS_FERRAMENTA.
    presets?: readonly string[];
}

const TUDO = { key: 'all', label: 'Todo o período' };
/** Atalhos das telas de ferramenta (regra do sênior: SMS, Analytics, Meta › Gerenciador): só Hoje, Ontem e 30 dias. */
export const ATALHOS_FERRAMENTA: readonly string[] = ['today', 'yesterday', 'last_30'];

// Converte Date -> "YYYY-MM-DD" (local, sem shift de fuso).
const formatToISO = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayNum = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayNum}`;
};

// Presets de periodo, calculados a partir de hoje.
const getPresets = (include15Days = false, includeRolling = false) => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    const lastMonth = subMonths(today, 1);
    const presets = [
        { key: 'today', label: 'Hoje', from: today, to: today },
        { key: 'yesterday', label: 'Ontem', from: yesterday, to: yesterday },
    ];
    if (includeRolling) {
        presets.push({ key: 'last_7', label: '7 dias', from: subDays(today, 6), to: today });
    }
    if (include15Days) {
        // 15 dias corridos, incluindo hoje (hoje - 14 dias).
        presets.push({ key: 'last_15', label: '15 dias', from: subDays(today, 14), to: today });
    }
    if (includeRolling) {
        presets.push({ key: 'last_30', label: '30 dias', from: subDays(today, 29), to: today });
    }
    presets.push(
        { key: 'this_month', label: 'Mês atual', from: startOfMonth(today), to: today },
        { key: 'last_month', label: 'Mês anterior', from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) },
    );
    return presets;
};

export function CompactDateRangePicker({ startDate, endDate, onDateChange, include15Days = false, includeRolling = false, maxRangeDays, presetsOnly = false, includeAllTime = false, presets }: CompactDateRangePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [tempFrom, setTempFrom] = useState<Date | undefined>(undefined);
    const [clickCount, setClickCount] = useState(0);
    const [rangeError, setRangeError] = useState<string | null>(null);

    const parseDate = (dateString: string): Date => {
        if (!dateString) return new Date(); // "Todo o período": sem data; o calendario parte de hoje
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const [selectedRange, setSelectedRange] = useState<{ from: Date; to: Date }>({
        from: parseDate(startDate),
        to: parseDate(endDate)
    });

    useEffect(() => {
        setSelectedRange({
            from: parseDate(startDate),
            to: parseDate(endDate)
        });
    }, [startDate, endDate]);

    const atalhos = getPresets(include15Days, includeRolling).filter((p) => !presets || presets.includes(p.key));
    // Qual preset corresponde ao periodo atual (ou null = personalizado).
    const tudoAtivo = includeAllTime && !startDate && !endDate;
    const activePreset = tudoAtivo ? TUDO.key : atalhos.find(
        (p) => formatToISO(p.from) === startDate && formatToISO(p.to) === endDate
    )?.key ?? null;
    const activeLabel = tudoAtivo ? TUDO.label : atalhos.find((p) => p.key === activePreset)?.label;

    const applyAllTime = () => {
        onDateChange('', '');
        setRangeError(null);
        setIsOpen(false);
        setShowCalendar(false);
    };

    const handleOpen = () => {
        setTempFrom(undefined);
        setClickCount(0);
        setRangeError(null);
        // Se o periodo atual ja e personalizado, abre direto no calendario.
        setShowCalendar(!presetsOnly && activePreset === null);
        setIsOpen(true);
    };

    const applyPreset = (from: Date, to: Date) => {
        setSelectedRange({ from, to });
        onDateChange(formatToISO(from), formatToISO(to));
        setRangeError(null);
        setIsOpen(false);
        setShowCalendar(false);
    };

    const handleDayClick = (day: Date) => {
        if (clickCount === 0) {
            setTempFrom(day);
            setClickCount(1);
            setRangeError(null);
        } else {
            const fromDate = tempFrom! <= day ? tempFrom! : day;
            const toDate = tempFrom! <= day ? day : tempFrom!;
            // Limite de periodo no modo Personalizado (se configurado).
            if (maxRangeDays && differenceInCalendarDays(toDate, fromDate) + 1 > maxRangeDays) {
                setRangeError(`Período máximo: ${maxRangeDays} dias. Selecione um intervalo menor.`);
                setTempFrom(undefined);
                setClickCount(0);
                return;
            }
            setSelectedRange({ from: fromDate, to: toDate });
            onDateChange(formatToISO(fromDate), formatToISO(toDate));
            setRangeError(null);
            setTimeout(() => {
                setIsOpen(false);
                setShowCalendar(false);
                setTempFrom(undefined);
                setClickCount(0);
            }, 200);
        }
    };

    // O painel abre ancorado a ESQUERDA do gatilho. Quando o gatilho esta perto
    // da borda direita da janela (header de tela, filtro no canto), ele vazava
    // pra fora — metade do calendario ficava inalcancavel. A escolha do lado e
    // medida, nao fixa: ancorar sempre a direita quebraria o caso oposto.
    const gatilhoRef = useRef<HTMLButtonElement>(null);
    const painelRef = useRef<HTMLDivElement>(null);
    const [ancora, setAncora] = useState<'left' | 'right'>('left');

    useLayoutEffect(() => {
        if (!isOpen) return;
        const gatilho = gatilhoRef.current;
        const painel = painelRef.current;
        if (!gatilho || !painel) return;
        const largura = painel.offsetWidth;
        const caixa = gatilho.getBoundingClientRect();
        const MARGEM = 8;
        // Depende so da posicao do gatilho e da largura do painel — nunca do
        // proprio `ancora`, senao a medicao entraria em loop.
        setAncora(caixa.left + largura + MARGEM > window.innerWidth ? 'right' : 'left');
        // showCalendar muda a largura do painel, entao remede.
    }, [isOpen, showCalendar]);

    const presetBtn = (active: boolean) =>
        `w-full flex items-center gap-2 text-left px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        }`;

    return (
        <div className="relative w-full sm:w-auto">
            <button
                ref={gatilhoRef}
                onClick={handleOpen}
                className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1 md:gap-2 px-2 md:px-3 py-1.5 bg-background border border-border rounded-md hover:border-foreground/40 transition-colors text-xs md:text-sm"
            >
                <Calendar className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium text-foreground whitespace-nowrap">
                    {activeLabel ?? (
                        `${format(selectedRange.from, 'dd/MM/yy', { locale: ptBR })} - ${format(selectedRange.to, 'dd/MM/yy', { locale: ptBR })}`
                    )}
                </span>
                <ChevronDown
                    className={`h-3 w-3 md:h-3.5 md:w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div
                        ref={painelRef}
                        className={`absolute top-full left-1/2 -translate-x-1/2 sm:translate-x-0 mt-1 z-50 bg-popover text-popover-foreground rounded-lg shadow-xl border border-border p-2 min-w-[190px] max-h-[70vh] overflow-y-auto ${
                            ancora === 'right' ? 'sm:left-auto sm:right-0' : 'sm:left-0 sm:right-auto'
                        }`}
                    >
                        {/* Presets rapidos */}
                        <div className="flex flex-col gap-0.5">
                            {atalhos.map((p) => (
                                <button key={p.key} type="button" onClick={() => applyPreset(p.from, p.to)} className={presetBtn(activePreset === p.key)}>
                                    {p.label}
                                </button>
                            ))}
                            {includeAllTime && (
                                <button type="button" onClick={applyAllTime} className={presetBtn(tudoAtivo)}>
                                    {TUDO.label}
                                </button>
                            )}
                            {!presetsOnly && (
                                <>
                                    <div className="my-1 border-t border-border" />
                                    <button
                                        type="button"
                                        onClick={() => setShowCalendar((v) => !v)}
                                        className={presetBtn(showCalendar || activePreset === null)}
                                    >
                                        <CalendarRange className="h-3.5 w-3.5 flex-shrink-0" />
                                        Personalizado
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Calendario — so no modo Personalizado */}
                        {showCalendar && (
                            <div className="mt-2 border-t border-border pt-2">
                                <div className="mb-2 text-xs text-muted-foreground flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                    {clickCount === 0 ? 'Clique na data inicial' : 'Clique na data final (pode ser a mesma)'}
                                </div>
                                {maxRangeDays && (
                                    <div className="mb-2 text-[11px] text-muted-foreground">
                                        Máximo de {maxRangeDays} dias por consulta.
                                    </div>
                                )}
                                {rangeError && (
                                    <div className="mb-2 text-xs text-red-600 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                        {rangeError}
                                    </div>
                                )}
                                <DayPicker
                                    mode="range"
                                    selected={
                                        tempFrom
                                            ? { from: tempFrom, to: tempFrom }
                                            : { from: selectedRange.from, to: selectedRange.to }
                                    }
                                    onDayClick={handleDayClick}
                                    locale={ptBR}
                                    numberOfMonths={1}
                                    className="rdp-compact"
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
            <style>{`
                .rdp-compact {
                    --rdp-cell-size: 36px;
                    --rdp-accent-color: #3b82f6;
                    --rdp-accent-background-color: rgba(59, 130, 246, 0.18);
                    --rdp-range_middle-background-color: rgba(59, 130, 246, 0.18);
                    --rdp-range_middle-color: hsl(var(--foreground));
                    --rdp-selected-color: #fff;
                    --rdp-today-color: #3b82f6;
                    font-size: 13px;
                    color: hsl(var(--foreground));
                }
                .rdp-compact .rdp-day_button { color: hsl(var(--foreground)); border-radius: 6px; }
                .rdp-compact .rdp-weekday, .rdp-compact .rdp-caption_label { color: hsl(var(--muted-foreground)); }
                .rdp-compact .rdp-button_previous, .rdp-compact .rdp-button_next { color: hsl(var(--foreground)); }
                .rdp-compact .rdp-button_previous:hover, .rdp-compact .rdp-button_next:hover { background-color: hsl(var(--accent)); }
                .rdp-compact .rdp-chevron { fill: currentColor; }
                .rdp-compact .rdp-day:hover:not(.rdp-selected) .rdp-day_button { background-color: hsl(var(--accent)); }
                .rdp-compact .rdp-range_middle { background-color: rgba(59, 130, 246, 0.18); }
                .rdp-compact .rdp-range_middle .rdp-day_button { color: hsl(var(--foreground)); }
                .rdp-compact .rdp-selected .rdp-day_button { background-color: #3b82f6; color: #fff; font-weight: 600; border: none; }
                .rdp-compact .rdp-today:not(.rdp-selected) .rdp-day_button { color: #3b82f6; font-weight: 600; }
                .rdp-compact .rdp-hidden { visibility: hidden; background-color: transparent !important; }
                .rdp-compact .rdp-outside.rdp-hidden { background-color: transparent !important; }
            `}</style>
        </div>
    );
}
